"use client"

import createMenuStructure from "@/components/create-menu-structure"
import BottomNav from "@/components/header/bottom-nav"
import { MobileScrollMenu } from "@/components/mobile-scroll-menu"
import { Images } from "@/constants/images"
import { useStickyHeaderList } from "@/hook/useHomepage"
import { Link as LocaleLink, usePathname } from "@/i18n/routing"
import { t } from "@/languages/i18n"
import { isItemActive, isMenuItemActive, linkCategoryPath } from "@/lib/config/lib/helper"
import { HeaderListProps } from "@/types/types"
import { CloseOutlined } from "@ant-design/icons"
import { Collapse, CollapseProps } from "antd"
import Image from "next/image"
import NextLink from "next/link"
import React, { useEffect, useRef, useState } from "react"

const HeaderList: React.FC<HeaderListProps> = ({
  menuData,
  locale,
  shouldBeTransparent = false,
  isScrolled = false,
  className = "",
  onSearchClick,
  socialLinks,
}) => {
  const pathname = usePathname()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false)
  const [menuActive, setMenuActive] = useState<string | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState("")
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const headerListRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const isSticky = useStickyHeaderList(headerListRef)
  const structuredMenu = createMenuStructure(menuData?.attributes?.items || [])

  // Keep for desktop menu functionality
  const activeMenu = structuredMenu?.find(item => item.title.toLowerCase() === menuActive?.toLowerCase())

  const handleMouseLeave = () => {
    setMenuActive(undefined)
    setIsSubMenuOpen(false)
  }

  const handleMenuDesktop = (menu: string) => {
    if (menuActive === menu) {
      setMenuActive(undefined)
      setIsSubMenuOpen(false)
    } else {
      setMenuActive(menu)
      setIsSubMenuOpen(true)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      window.location.href = `/${locale}/search?q=${encodeURIComponent(searchQuery)}`
      setIsMenuOpen(false)
    }
  }

  const MenuMobile = () => {
    const menuCollapseItems: CollapseProps["items"] = structuredMenu?.map((item, index) => {
      const isLastItem = index === (structuredMenu?.length || 0) - 1
      const itemKey = item.id || `menu-${index}`
      const hasChildren = item.attribute && item.attribute.length > 0

      if (!hasChildren) {
        return {
          key: itemKey,
          label: (
            <LocaleLink
              href={linkCategoryPath(item.url)}
              className="block p-1.5 pl-0 pt-0 text-base leading-6 font-bold text-[#333333]"
              onClick={() => setIsMenuOpen(false)}
            >
              {item.title}
            </LocaleLink>
          ),
          showArrow: false,
          collapsible: "disabled",
          style: isLastItem ? undefined : { borderBottom: "1px solid #e5e7eb" },
        }
      }

      return {
        key: itemKey,
        label: (
          <LocaleLink
            href={linkCategoryPath(item.url)}
            className="block p-1.5 pl-0 pt-0 text-base leading-6 font-bold text-[#333333]"
            onClick={() => setIsMenuOpen(false)}
          >
            {item.title}
          </LocaleLink>
        ),
        children: (
          <div className="flex flex-col">
            {item.attribute?.map((subItem: any, subIndex: number) => {
              const hasGrandChildren = subItem.attribute && subItem.attribute.length > 0

              if (!hasGrandChildren) {
                return (
                  <LocaleLink
                    key={subIndex}
                    href={linkCategoryPath(subItem.url)}
                    className="px-4 py-3 text-sm leading-5 font-normal text-[#666666] hover:text-[#008746]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {subItem.title}
                  </LocaleLink>
                )
              }

              return (
                <div key={subIndex}>
                  <div className="px-4 py-3 text-sm leading-5 font-normal text-[#666666]">
                    <LocaleLink
                      href={linkCategoryPath(subItem.url)}
                      className="hover:text-[#008746]"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {subItem.title}
                    </LocaleLink>
                  </div>
                  <div className="pl-4">
                    {subItem.attribute.map((grandChild: any, grandIndex: number) => (
                      <LocaleLink
                        key={grandIndex}
                        href={linkCategoryPath(grandChild.url)}
                        className="block px-4 py-2 text-xs leading-4 font-normal text-[#999999] hover:text-[#008746]"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {grandChild.title}
                      </LocaleLink>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        ),
        style: isLastItem ? undefined : { borderBottom: "1px solid #e5e7eb" },
      }
    })

    return (
      <>
        {isMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-[90] top-0" />}

        <div
          className={`w-[80%] bg-white transform transition-all duration-300 ease-out will-change-transform h-screen fixed top-0 left-0 overflow-y-auto ${
            isMenuOpen ? "translate-x-0 z-[100]" : "-translate-x-full z-[100]"
          }`}
        >
          <div className="flex items-center justify-end gap-3 p-1 pb-0">
            <LocaleLink href="/" className="relative w-[180px] h-[31px] mb-[0.375rem]">
              <Image src={Images.logoTamdaMedia} alt={t("TamdaMedia Logo")} fill className="object-contain" />
            </LocaleLink>
            <button
              onClick={() => {
                setIsMenuOpen(false)
                setExpandedKeys([])
              }}
              className="p-2 pr-3"
            >
              <CloseOutlined className="text-2xl text-[#37B24D]" />
            </button>
          </div>
          <div className="h-px bg-gray-200" />

          <div className="p-2 pb-0">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder={t("Search")}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full h-[2.25rem] text-[#242F3E] rounded-sm px-4 py-1 pt-0 pr-12 text-base focus:outline-none focus:border-[#008746] bg-[#F5F5F5]"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 pb-[0.25px] hover:opacity-70 transition-opacity w-[1.1875rem] h-[1.1875rem]"
                aria-label={t("Search")}
              >
                <Image src={Images.tamdaSearchIcon} alt={t("Search")} fill className="object-contain" />
              </button>
            </form>
          </div>

          <div className="flex items-center justify-between px-4 pt-1">
            <NextLink
              href={"tel:+420771268888"}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              aria-label={t("Phone")}
            >
              <Image src={Images.phoneIcon} alt={t("Phone")} width={24} height={24} className="w-6 h-6" />
            </NextLink>

            <div className="flex items-center">
              {socialLinks?.facebook && (
                <NextLink
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  aria-label={t("Facebook")}
                >
                  <Image
                    src={socialLinks.facebookIcon || Images.facebookIcon}
                    alt={t("Facebook")}
                    width={24}
                    height={24}
                    className="w-6 h-6"
                  />
                </NextLink>
              )}
              {socialLinks?.youtube && (
                <NextLink
                  href={socialLinks.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  aria-label={t("Youtube")}
                >
                  <Image
                    src={socialLinks.youtubeIcon || Images.youtubeIcon}
                    alt={t("Youtube")}
                    width={24}
                    height={24}
                    className="w-6 h-6"
                  />
                </NextLink>
              )}
            </div>
          </div>

          <div className="h-px bg-gray-200 mx-2" />

          <div className="px-4 py-2">
            <Collapse
              ghost
              items={menuCollapseItems}
              activeKey={expandedKeys}
              onChange={keys => setExpandedKeys(Array.isArray(keys) ? keys : [keys])}
            />
          </div>
        </div>
      </>
    )
  }

  useEffect(() => {
    setIsMenuOpen(false)
    setIsSubMenuOpen(false)
    setMenuActive(undefined)
    setExpandedKeys([])
  }, [pathname])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setMenuActive(undefined)
        setIsSubMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div ref={headerRef} className="relative">
      {isSticky && <div className="h-[3rem]" />}
      <div
        ref={headerListRef}
        data-header-list
        // Khi sticky (fixed top-0), dời xuống dưới admin-bar (nếu có) qua biến
        // `--admin-bar-h`; mặc định 0 nên không ảnh hưởng khi không có bar.
        style={isSticky ? { top: "var(--admin-bar-h, 0px)" } : undefined}
        className={`w-full bg-[#008746] shadow-header-custom transition-all duration-300 ${
          isSticky ? "fixed top-0 left-0 right-0 z-50" : "relative"
        } ${className}`}
      >
        <div className="lg:w-[1128px] mx-auto min-[1180px]:h-[3rem] flex items-center">
          <div className="flex items-center justify-start relative">
            <nav className="hidden min-[1180px]:flex items-center justify-start ">
              {structuredMenu?.map(item => {
                if (item.attribute && item.attribute.length > 0) {
                  return (
                    <div
                      key={item.id}
                      className="relative flex items-center cursor-pointer group"
                      onClick={() => handleMenuDesktop(item.title)}
                      onMouseEnter={() => {
                        setMenuActive(item.title)
                        setIsSubMenuOpen(true)
                      }}
                    >
                      <LocaleLink
                        href={linkCategoryPath(item.url)}
                        className={`text-white text-base font-medium leading-[1.55] px-4 max-[1380px]:px-3 h-[3rem] flex items-center hover:bg-[#1EAD46] hover:brightness-[1.1] hover:filter hover:text-white transition-all duration-300 whitespace-nowrap ${
                          menuActive === item.title || isMenuItemActive(item, pathname)
                            ? "bg-[#19B04E] filter brightness-[1.1]"
                            : ""
                        }`}
                      >
                        {item.title}
                      </LocaleLink>
                      {menuActive === item.title && isSubMenuOpen && (
                        <div
                          className="absolute top-full left-0 z-50 bg-[#008746] shadow-lg w-auto"
                          onMouseLeave={handleMouseLeave}
                        >
                          <div className="">
                            <div className="flex flex-col">
                              {item.attribute.map(subItem => (
                                <div key={subItem.id} className="flex flex-col">
                                  <LocaleLink
                                    href={linkCategoryPath(subItem.url)}
                                    className="text-white text-base font-medium py-2 px-4 h-[3rem] hover:bg-[#1EAD46] hover:brightness-[1.1] hover:filter hover:text-white transition-all duration-200 whitespace-nowrap"
                                  >
                                    {subItem.title}
                                  </LocaleLink>
                                  {subItem.attribute && subItem.attribute.length > 0 && (
                                    <div className="flex flex-col ml-4">
                                      {subItem.attribute.map(child => (
                                        <LocaleLink
                                          key={child.id}
                                          href={linkCategoryPath(child.url)}
                                          className="text-white text-base font-medium py-2 px-4 hover:bg-[#1EAD46] hover:brightness-[1.1] hover:filter hover:text-white transition-all duration-200 whitespace-nowrap"
                                        >
                                          {child.title}
                                        </LocaleLink>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                } else {
                  return (
                    <LocaleLink
                      key={item.id}
                      href={linkCategoryPath(item.url)}
                      className={`text-white text-base font-medium leading-[24.8px] px-4 max-[1380px]:px-3 h-[3rem] flex items-center hover:bg-[#1EAD46] hover:brightness-[1.1] hover:filter hover:text-white transition-all duration-300 whitespace-nowrap ${
                        isItemActive(item.url, pathname) ? "bg-[#19B04E] filter brightness-[1.1]" : ""
                      }`}
                      onMouseEnter={() => {
                        setMenuActive(undefined)
                        setIsSubMenuOpen(false)
                      }}
                    >
                      {item.title}
                    </LocaleLink>
                  )
                }
              })}
            </nav>

            <div className="hidden">
              <span className="text-white text-sm font-bold leading-[1.55rem]">MENU</span>
            </div>
          </div>
        </div>
      </div>
      <div className="min-[1180px]:hidden block w-full z-40 bg-white">
        <MobileScrollMenu menuItems={structuredMenu} />
      </div>
      {MenuMobile()}

      <BottomNav
        locale={locale}
        onMenuClick={() => {
          setIsMenuOpen(!isMenuOpen)
          setIsSubMenuOpen(false)
        }}
        socialLinks={socialLinks}
      />
    </div>
  )
}

export default HeaderList
